import {Box, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {useEffect, useMemo, useRef, useState} from "react";
import ItemPopover from "../ItemPopover";

const locale = localStorage.getItem('tm-locale') || 'en-gb';

const dayLabel = [
    'SUN',
    'MON',
    'TUE',
    'WED',
    'THU',
    'FRI',
    'SAT',
];

export default function Weekday(props) {
    const {
        day,
        weekDate,
        //events,
        //tasks,
        periods,
        periodWindowSize,
        addScrollCallback,
        scrollCallback,
        beginEdit,
        submitDelete,
        markComplete,
        openCreateMenu,
        taskSlots,
        eventSlots,
        taskGridHeight,
        eventGridHeight,
        tasksCollapsed,
        eventsCollapsed,
        labels,
        independentLabel,
        draggingPeriod,
        setDraggingPeriod,
        alterItem,
        mobile,
    } = props;

    const date = useMemo(() => {
        const date = new Date(weekDate.getTime());
        date.setDate(weekDate.getDate() + day);
        return date;
    }, [day, weekDate]);

    const [currentDate, setCurrentDate] = useState(() => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 1);
        return currentDate;
    });

    const isToday = useMemo(() => currentDate.getTime() === date.getTime(), [currentDate, date]);

    const endDate = new Date(date.getTime());
    endDate.setHours(23, 59, 59);

    const beginDate = new Date(date.getTime());
    beginDate.setHours(0, 0, 1);

    //const activeEvents = events.filter(e => e.start <= endDate && beginDate <= e.end);
    //const activeTasks = tasks.filter(t => t.start <= endDate && beginDate <= t.end);
    const activePeriods = periods.filter(p => p.start <= endDate && beginDate <= p.end);

    //let eventsAdded = 0;
    //let tasksAdded = 0;

    const periodWindowRef = useRef();

    const [expandedMenuAnchor, setExpandedMenuAnchor] = useState(null);
    const [expandedItem, setExpandedItem] = useState(null);
    const [expandedType, setExpandedType] = useState(null);

    const [timeMarkerPosition, setTimeMarkerPosition] = useState(null);

    const [prepareDrag, setPrepareDrag] = useState(null);
    const [dragPosition, setDragPosition] = useState(null);

    const onScrollWindow = () => {
        scrollCallback(periodWindowRef.current.scrollTop);
    };

    useEffect(() => {
        const preventTouchMove = (e) => e.preventDefault();

        if (mobile && (prepareDrag !== null || draggingPeriod !== null)) {
            window.addEventListener('touchmove', preventTouchMove, { passive: false });

            return () => window.removeEventListener('touchmove', preventTouchMove);
        }
    }, [mobile, prepareDrag, draggingPeriod]);

    useEffect(() => {
        addScrollCallback((scroll) => {
            if (dragPosition !== null) {
                return;
            }

            periodWindowRef.current.scrollTop = scroll;
        });
    }, [addScrollCallback, periodWindowRef, dragPosition]);

    useEffect(() => {
        const calculateTimeMarkerPosition = () => {
            const dayTime = new Date().getTime() - currentDate.getTime();
            setTimeMarkerPosition(dayTime / (60 * 1000)); // in minutes
        };

        if (isToday) {
            calculateTimeMarkerPosition();
            const intervalId = setInterval(calculateTimeMarkerPosition, 60 * 1000);
            return () => clearInterval(intervalId);
        }
    }, [setTimeMarkerPosition, isToday, currentDate]);

    const expand = (type, item) => {
        return (e) => {
            setExpandedItem(item);
            setExpandedType(type);
            setExpandedMenuAnchor(e.currentTarget);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentDate(() => {
                const currentDate = new Date();
                currentDate.setHours(0, 0, 0, 1);
                return currentDate;
            })
        }, 24 * 60 * 60 * 1000 - (new Date().getTime() - currentDate.getTime()));

        return () => clearTimeout(timeoutId);
    }, [currentDate, date, setCurrentDate]);

    return (
        <Grid2
            sm={1}
            sx={{
                background: '#1d1d1d',
                height: '100%',
                borderRight: 'solid 1px #444',
            }}
        >
            <Box
                sx={{
                    maxHeight: '100%',
                    height: '100%',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '30px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: 'solid 1px #444',
                        borderColor: isToday ? '#24bffa' : undefined,
                        userSelect: 'none',
                    }}
                >
                    <Typography
                        color={isToday ? '#24bffa' : 'white'}
                        fontFamily="'Inter', sans-serif"
                        fontWeight="700"
                    >
                        {dayLabel[day]}. &nbsp; {date.toLocaleString(locale, { month: 'numeric', day: 'numeric' })}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        height: `${eventGridHeight * (eventsCollapsed ? 8 : 33) + 9}px`,
                        borderTop: 'solid 1px #00000000',
                        borderBottom: 'solid 1px #444',
                        minHeight: '90px',
                        cursor: 'crosshair',
                    }}
                    onClick={() => {
                        const startDate = new Date(beginDate.getTime());
                        startDate.setHours(0, 0);

                        // Set end date one hour from start.
                        const endDate = new Date(startDate.getTime());
                        endDate.setHours(1, 0);

                        openCreateMenu('Event', { start: startDate, end: endDate })();
                    }}
                >
                    {eventSlots.map((slot, index) => {
                        if (slot) {
                            const labelKey = slot.event.labels?.[0] ? parseInt(slot.event.labels?.[0]) : 0;
                            const label = labels.find((label) => label.key === labelKey);

                            return (
                                <Box
                                    key={slot.event.name + slot.event.key}
                                    sx={{
                                        position: 'absolute',
                                        left: slot.start ? '3px' : '0',
                                        right: slot.end ? '3px' : '-1px',
                                        top: `${3 + Math.max(index * (eventsCollapsed ? 8 : 33), 0)}px`,
                                        height: eventsCollapsed ? '5px' : '30px',
                                        border: 'hidden',
                                        borderTopLeftRadius: slot.start ? '15px' : '0',
                                        borderBottomLeftRadius: slot.start ? '15px' : '0',
                                        borderTopRightRadius: slot.end ? '15px' : '0',
                                        borderBottomRightRadius: slot.end ? '15px' : '0',
                                        background: label?.color || '#474747',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    onClick={(e) => {
                                        expand('Event', slot.event)(e);
                                        e.stopPropagation();
                                    }}
                                >
                                    {(date.getDate() === weekDate.getDate() || slot.start || independentLabel) && !eventsCollapsed &&
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                maxWidth: `calc(${(slot.remaining + 1) * 100}% - 20px)`,
                                            }}
                                        >
                                            <Typography
                                                ml="12px"
                                                color="white"
                                                fontSize="10pt"
                                                fontWeight="700"
                                                fontFamily="'Roboto', sans-serif"
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    zIndex: '200',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {slot.event.name}
                                            </Typography>
                                        </Box>
                                    }
                                </Box>
                            );
                        } else {
                            return undefined;
                        }
                    })}
                    {/*{activeEvents.map((e) => {
                        const start = e.start.getDate() === date.getDate();
                        const end = e.end.getDate() === date.getDate();

                        let verticalOffset = -eventsAdded;

                        for (let i = 0; events[i] !== e; ++i) {

                            const iEvent = events[i];

                            const eventStart = new Date(iEvent.start);
                            eventStart.setHours(0, 0, 1);

                            const eventEnd = new Date(iEvent.end);
                            eventEnd.setHours(23, 59, 59);

                            if (eventStart <= e.end && eventEnd >= e.start) {
                                verticalOffset++;
                            }
                        }

                        eventsAdded += verticalOffset + 1;

                        return (
                            <Box
                                key={e.name}
                                sx={{
                                    width: `calc(100% + (${(start ? -3 : 0.5) + end ? -3 : 0.5 }px))`,
                                    height: '30px',
                                    border: 'hidden',
                                    borderTopLeftRadius: start ? '5px' : '0',
                                    borderBottomLeftRadius: start ? '5px' : '0',
                                    borderTopRightRadius: end ? '5px' : '0',
                                    borderBottomRightRadius: end ? '5px' : '0',
                                    marginTop: `${3 + verticalOffset * 33}px`,
                                    marginLeft: start ? '3px' : '0',
                                    marginRight: end ? '3px' : '0',
                                    background: '#193255',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={expand('Event', e)}
                            >
                                {(date.getDate() === weekDate.getDate() || start) &&
                                    <Typography
                                        ml="7px"
                                        color="white"
                                        fontSize="10pt"
                                        fontWeight="700"
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            zIndex: '200'
                                        }}
                                    >
                                        {e.name}
                                    </Typography>
                                }
                            </Box>
                        );
                    })}*/}
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        height: `${(taskGridHeight) * (tasksCollapsed ? 8 : 33) + 9}px`,
                        borderTop: 'solid 1px #00000000',
                        borderBottom: 'solid 1px #444',
                        minHeight: '90px',
                        userSelect: 'none',
                        cursor: 'crosshair',
                    }}
                    onClick={() => {
                        const startDate = new Date(beginDate.getTime());
                        startDate.setHours(0, 0);

                        // Set end date one hour from start.
                        const endDate = new Date(startDate.getTime());
                        endDate.setHours(1, 0);

                        openCreateMenu('Task', { start: startDate, end: endDate })();
                    }}
                >
                    {taskSlots.map((slot, index) => {
                        if (slot) {
                            const labelKey = slot.task.labels?.[0] ? parseInt(slot.task.labels?.[0]) : 0;
                            const label = labels.find((label) => label.key === labelKey);

                            const baseColor = label?.color || '#474747';

                            return (
                                <Box
                                    key={slot.task.name + slot.task.key}
                                    sx={{
                                        position: 'absolute',
                                        left: slot.start ? '3px' : '0',
                                        right: slot.end ? '3px' : '-1px',
                                        top: `${3 + Math.max(index * (tasksCollapsed ? 8 : 33), 0)}px`,
                                        height: tasksCollapsed ? '5px' : '30px',
                                        border: 'hidden',
                                        borderTopLeftRadius: slot.start ? '15px' : '0',
                                        borderBottomLeftRadius: slot.start ? '15px' : '0',
                                        borderTopRightRadius: slot.end ? '15px' : '0',
                                        borderBottomRightRadius: slot.end ? '15px' : '0',
                                        background: slot.task.complete ? `rgb(from ${baseColor} r g b / 0.4)` : baseColor,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        //filter: slot.task.complete ? 'brightness(0.8)' : 'none',
                                    }}
                                    onClick={(e) => {
                                        expand('Task', slot.task)(e);
                                        e.stopPropagation();
                                    }}
                                >
                                    {(date.getDate() === weekDate.getDate() || slot.start || independentLabel) && !tasksCollapsed &&
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                maxWidth: `calc(${(slot.remaining + 1) * 100}% - 20px)`,
                                            }}
                                        >
                                            <Typography
                                                ml="12px"
                                                color={slot.task.complete ? '#b5b5b5' : '#ffffff'}
                                                fontSize="10pt"
                                                fontWeight="700"
                                                fontFamily="'Roboto', sans-serif"
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    zIndex: '200',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    textDecoration: slot.task.complete && 'line-through'
                                                }}
                                            >
                                                {slot.task.name}
                                            </Typography>
                                        </Box>
                                    }
                                </Box>
                            );
                        } else {
                            return undefined;
                        }
                    })}
                    {/*{activeTasks.map((t) => {
                        const start = t.start.getDate() === date.getDate();
                        const end = t.end.getDate() === date.getDate();

                        let verticalOffset = -tasksAdded;

                        for (let i = 0; tasks[i] !== t; ++i) {
                            const iTask = tasks[i];

                            const taskStart = new Date(iTask.start);
                            taskStart.setHours(0, 0, 1);

                            const taskEnd = new Date(iTask.end);
                            taskEnd.setHours(23, 59, 59);

                            if (taskStart <= t.end && taskEnd >= t.start) {
                                verticalOffset++;
                            }
                        }

                        tasksAdded += verticalOffset + 1;

                        return (
                            <Box
                                key={t.name}
                                sx={{
                                    width: start || end ? 'calc(100% - 2px)' : 'calc(100% + 1px)',
                                    height: '30px',
                                    border: 'hidden',
                                    borderTopLeftRadius: start ? '5px' : '0',
                                    borderBottomLeftRadius: start ? '5px' : '0',
                                    borderTopRightRadius: end ? '5px' : '0',
                                    borderBottomRightRadius: end ? '5px' : '0',
                                    marginTop: `${3 + Math.max(verticalOffset * 33, 0)}px`,
                                    marginLeft: start ? '3px' : '0',
                                    marginRight: end ? '3px' : '0',
                                    background: t.complete ? '#503524' : '#714023',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={expand('Task', t)}
                            >
                                {(date.getDate() === weekDate.getDate() || start) &&
                                    <Typography
                                        ml="7px"
                                        color={t.complete ? '#b5b5b5' : '#ffffff'}
                                        fontSize="10pt"
                                        fontWeight="700"
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            zIndex: '200'
                                        }}
                                    >
                                        {t.name}
                                    </Typography>
                                }
                            </Box>
                        );
                    })}*/}
                </Box>
                <Box
                    ref={periodWindowRef}
                    sx={{
                        overflowY: 'scroll',
                        '::-webkit-scrollbar': {
                            display: 'none',
                        },
                        height: `${periodWindowSize}px`,
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        cursor: 'crosshair',
                        touchAction: prepareDrag !== null && 'none',
                    }}
                    onScroll={onScrollWindow}
                    onClick={dragPosition === null && draggingPeriod === null ? (e) => {
                        // Round minutes to nearest 15 minutes.
                        const totalMinutes = Math.round((e.clientY - e.currentTarget.offsetTop + e.currentTarget.scrollTop) / 15) * 15;
                        const hours = Math.floor(totalMinutes / 60);
                        const minutes = totalMinutes % 60;

                        const startDate = new Date(beginDate.getTime());
                        startDate.setHours(hours, minutes);

                        // Set end date one hour from start.
                        const endDate = new Date(startDate.getTime());
                        endDate.setHours(Math.min(hours + 1, 23), minutes);

                        openCreateMenu('Period', { start: startDate, end: endDate })();
                    } : () => {
                        setDraggingPeriod(null);
                        setDragPosition(null);
                    }}
                    onPointerMove={draggingPeriod !== null ? (e) => {
                        if ((e.buttons & 0x1) === 0) {
                            setDraggingPeriod(null);
                            setDragPosition(null);
                        }

                        const roundIncrement = e.shiftKey ? 5 : 15;

                        const rawPosition = e.clientY - e.currentTarget.offsetTop + e.currentTarget.scrollTop;
                        // Round to nearest 15 minutes;
                        setDragPosition(Math.round(rawPosition / roundIncrement) * roundIncrement);
                    } : undefined}
                    onPointerLeave={dragPosition !== null ? () => setDragPosition(null) : undefined}
                    onPointerUp={dragPosition !== null && draggingPeriod !== null ? () => {
                        const duration = draggingPeriod.duration || (draggingPeriod.end - draggingPeriod.start) / 1000 / 60;

                        const newStartTime = new Date(date.getTime() + dragPosition * 1000 * 60);
                        const newEndTime = new Date(newStartTime.getTime() + duration * 1000 * 60);

                        if (draggingPeriod.duration === undefined && Math.abs(newStartTime.getTime() - draggingPeriod.start.getTime()) / 60 / 1000 < 5) {
                            if (mobile) {
                                setDraggingPeriod(null);
                                setDragPosition(null);
                            }
                            return;
                        }

                        alterItem('Period', {
                            ...draggingPeriod,
                            start: newStartTime,
                            end: newEndTime,
                        });

                        setDraggingPeriod(null);
                    } : undefined}
                >
                    <div
                        style={{
                            position: 'relative',
                            height: `calc(24 * 60px + 20px${mobile ? ' + 60px' : ''})`,
                        }}
                    >
                        {isToday &&
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: `${timeMarkerPosition - 7}px`,
                                    left: '0',
                                    right: '0',
                                    height: `0px`,
                                    borderLeft: 'solid 7px #24bffa',
                                    borderRight: 'solid 7px #24bffa',
                                    borderTop: 'solid 7px transparent',
                                    borderBottom: 'solid 7px transparent',
                                    //boxShadow: 'inset 7px 0 #24bffa, inset -7px 0 #24bffa',
                                    zIndex: 100,
                                    pointerEvents: 'none',
                                }}
                            />
                        }
                        {activePeriods.map((p) => {
                            const labelKey = p.labels?.[0] ? parseInt(p.labels?.[0]) : 0;
                            const label = labels.find((label) => label.key === labelKey);

                            const duration = (p.end - p.start) / 1000 / 60;
                            const position = (p.start.getHours() * 60 + p.start.getMinutes()) + 1;

                            return (
                                <Box
                                    key={p.name + p.key}
                                    sx={{
                                        position: 'absolute',
                                        top: `${position}px`,
                                        left: '4px',
                                        right: '4px',
                                        height: `${duration - 2}px`,
                                        minHeight: `${duration - 2}px`,
                                        background: label?.color || '#474747', //'#1a5038',
                                        borderRadius: '7px',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        userSelect: 'none',
                                        '&:hover': duration < 50 ? {
                                            height: 'auto',
                                            pb: '5px',
                                            zIndex: 75,
                                            filter: 'drop-shadow(0 0 0.75rem #111)',
                                            '& > *': {
                                                opacity: 1
                                            }
                                        } : {
                                            zIndex: 75,
                                            filter: 'drop-shadow(0 0 0.75rem #111)',
                                        },
                                        zIndex: 50,
                                        filter: draggingPeriod === p ? 'opacity(0.5)' : undefined,
                                        pointerEvents: draggingPeriod !== null && 'none',
                                    }}
                                    onClick={(e) => {
                                        expand('Period', p)(e)
                                        e.stopPropagation();
                                    }}
                                    onPointerMove={prepareDrag !== null ? (e) => {
                                        if (p === prepareDrag[0] && Math.abs(e.clientY - prepareDrag[1]) >= 5) {
                                            setDraggingPeriod(p);
                                            setDragPosition(position);
                                            setPrepareDrag(null);
                                        }
                                    } : undefined}
                                    onPointerLeave={prepareDrag !== null ? () => {
                                        if (p === prepareDrag[0]) {
                                            setDraggingPeriod(p);
                                            setDragPosition(position);
                                            setPrepareDrag(null);
                                        }
                                    } : undefined}
                                    onPointerUp={() => setPrepareDrag(null)}
                                    onPointerDown={!mobile ? (e) => {
                                        setPrepareDrag([p, e.clientY]);
                                        e.stopPropagation();
                                    } : undefined}
                                >
                                    <Typography
                                        m="5px 7px 0 9px"
                                        color="white"
                                        fontSize="10pt"
                                        fontWeight="700"
                                        fontFamily="'Inter', sans-serif"
                                        height="20px"
                                        p="0"
                                        sx={{
                                            display: mobile && 'inline-flex',
                                            textWrap: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            opacity: duration < 30 ? '0' : '1'
                                        }}
                                        onPointerDown={mobile ? (e) => {
                                            setPrepareDrag([p, e.clientY]);
                                            e.stopPropagation();
                                        } : undefined}
                                    >
                                        {p.name}
                                    </Typography>
                                    <Typography
                                        m="0 7px 0 9px"
                                        color="white"
                                        fontSize="10pt"
                                        fontWeight="400"
                                        p="0"
                                        sx={{
                                            opacity: duration < 50 ? '0' : '1'
                                        }}
                                    >
                                        {p.start.toLocaleString(locale, { hour: 'numeric', minute: '2-digit' })} -&nbsp;
                                        {p.end.toLocaleString(locale, { hour: 'numeric', minute: '2-digit' })}
                                    </Typography>
                                </Box>
                            );
                        })}
                        {dragPosition !== null && draggingPeriod !== null && (() => {
                            const labelKey = draggingPeriod.labels?.[0] ? parseInt(draggingPeriod.labels?.[0]) : 0;
                            const label = labels.find((label) => label.key === labelKey);

                            const duration = draggingPeriod.duration || (draggingPeriod.end - draggingPeriod.start) / 1000 / 60;

                            const newStartTime = new Date(date.getTime() + dragPosition * 1000 * 60);
                            const newEndTime = new Date(newStartTime.getTime() + duration * 1000 * 60);

                            return <Box
                                sx={{
                                    position: 'absolute',
                                    top: `${dragPosition}px`,
                                    left: '4px',
                                    right: '4px',
                                    height: duration <= 50 ? 'auto' : `${duration - 2}px`,
                                    pb: duration <= 50 && '5px',
                                    background: label?.color || '#474747', //'#1a5038',
                                    borderRadius: '7px',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    userSelect: 'none',
                                    zIndex: 50,
                                    pointerEvents: 'none',
                                    filter: 'drop-shadow(0 0 0.75rem #111)',
                                }}
                            >
                                <Typography
                                    m="5px 7px 0 9px"
                                    color="white"
                                    fontSize="10pt"
                                    fontWeight="700"
                                    fontFamily="'Inter', sans-serif"
                                    height="20px"
                                    p="0"
                                    sx={{
                                        textWrap: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {draggingPeriod.name}
                                </Typography>
                                <Typography
                                    m="0 7px 0 9px"
                                    color="white"
                                    fontSize="10pt"
                                    fontWeight="400"
                                    p="0"
                                >
                                    {newStartTime.toLocaleString(locale, {hour: 'numeric', minute: '2-digit'})} -&nbsp;
                                    {newEndTime.toLocaleString(locale, {hour: 'numeric', minute: '2-digit'})}
                                </Typography>
                            </Box>
                        })()}
                    </div>
                </Box>
            </Box>

            <ItemPopover
                anchor={expandedMenuAnchor}
                onClose={() => setExpandedMenuAnchor(null)}
                type={expandedType}
                item={expandedItem}
                beginDate={beginDate}
                endDate={endDate}
                beginEdit={beginEdit}
                openCreateMenu={openCreateMenu}
                markComplete={markComplete}
                submitDelete={submitDelete}
                alterItem={alterItem}
                mobile={mobile}
            />
        </Grid2>
    );
}